/**
 * authStore.ts — SPINEX Predict Admin Auth Store
 *
 * Fixes vs original:
 *  1. Original store had no persistence — a hard reload always forced re-login
 *     even though the token was still in localStorage. Now we rehydrate user
 *     from localStorage on module initialisation so ProtectedRoute works
 *     correctly after a browser refresh.
 *  2. logout() now also clears the refresh token via setAdminToken(null, null).
 *  3. isAuthenticated was computed only from in-memory state; now it also
 *     checks the persisted token so it's accurate on first render.
 *  4. Added hasRehydrated flag so ProtectedRoute can gate on it and avoid
 *     bouncing the user during the initial tick.
 */

import { create } from 'zustand'
import { setAdminToken } from '@/api/adminApi'

const USER_KEY = 'spinex_admin_user'

interface AdminUserState {
  id: string
  email: string
  role: string
}

interface AuthStore {
  user: AdminUserState | null
  token: string | null
  isAuthenticated: boolean
  hasRehydrated: boolean
  login: (user: AdminUserState, token: string) => void
  logout: () => void
}

// Rehydrate from localStorage on module load
function rehydrate(): Pick<AuthStore, 'user' | 'token' | 'isAuthenticated' | 'hasRehydrated'> {
  try {
    const raw = localStorage.getItem(USER_KEY)
    const token = localStorage.getItem('spinex_admin_token')
    if (raw && token) {
      const user = JSON.parse(raw) as AdminUserState
      return { user, token, isAuthenticated: true, hasRehydrated: true }
    }
  } catch {
    // corrupt storage — ignore
  }
  return { user: null, token: null, isAuthenticated: false, hasRehydrated: true }
}

export const useAdminAuth = create<AuthStore>((set) => ({
  ...rehydrate(),

  login(user, token) {
    localStorage.setItem(USER_KEY, JSON.stringify(user))
    set({ user, token, isAuthenticated: true, hasRehydrated: true })
  },

  logout() {
    localStorage.removeItem(USER_KEY)
    setAdminToken(null, null)
    set({ user: null, token: null, isAuthenticated: false, hasRehydrated: true })
  },
}))
